import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from "../../../components/ui/Button";

export const YOUR_WHATSAPP_NUMBER = "+917304660050";

const ServiceComparison = () => {
  const [selectedServices, setSelectedServices] = useState(['it-infrastructure', 'cloud-migration']);

  const services = [
    { id: 'it-infrastructure', name: 'IT Infrastructure', icon: 'Server' },
    { id: 'cloud-migration', name: 'Cloud Migration', icon: 'Cloud' },
    { id: 'cybersecurity', name: 'Cybersecurity', icon: 'Shield' },
    { id: 'brand-strategy', name: 'Brand Strategy', icon: 'Palette' }
  ];

  const comparisonData = {
    'it-infrastructure': {
      implementation: '4-6 weeks',
      support: '24/7 Premium',
      pricing: 'Enterprise',
      roi: '35% savings',
      features: ['Server Management', 'Network Setup', 'Hardware Procurement', 'Monitoring']
    },
    'cloud-migration': {
      implementation: '6-8 weeks',
      support: '24/7 Premium',
      pricing: 'Custom',
      roi: '40% cost reduction',
      features: ['AWS/Azure Setup', 'Data Migration', 'Security Config', 'Optimization']
    },
    'cybersecurity': {
      implementation: '3-4 weeks',
      support: '24/7 Critical',
      pricing: 'Premium',
      roi: '60% risk reduction',
      features: ['Threat Detection', 'Compliance', 'Penetration Testing', 'Training']
    },
    'brand-strategy': {
      implementation: '1-2 weeks',
      support: 'Business Hours',
      pricing: 'Project-based',
      roi: '50% brand value increase',
      features: ['Market Research', 'Identity Design', 'Guidelines', 'Launch Support']
    }
  };

  const toggleService = (serviceId) => {
    if (selectedServices?.includes(serviceId)) {
      if (selectedServices?.length > 1) {
        setSelectedServices(selectedServices?.filter(id => id !== serviceId));
      }
    } else {
      if (selectedServices?.length < 3) {
        setSelectedServices([...selectedServices, serviceId]);
      }
    }
  };

  return (

    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-muted/30 text-white">
      <div className="max-w-7xl mx-auto">
        
        <div className="text-center mb-8 md:mb-12 ">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="GitCompare" size={20} color="var(--color-primary)" />
            <span className="text-sm font-semibold text-primary">Service Comparison</span>
          </div>
          <h2 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Compare Our Services
          </h2>
          <p className="text-white text-sm md:text-base text-text-secondary max-w-2xl mx-auto">
            Select up to 3 services to compare features, pricing, and implementation timelines
          </p>
        </div>
        
        <div className="bg-card rounded-xl shadow-lg p-4 md:p-6 lg:p-8">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-3">
              Select Services to Compare (Max 3)
            </label>
            <div className="flex flex-wrap gap-2">
              {services?.map((service) => (
                <button
                  key={service?.id}
                  onClick={() => toggleService(service?.id)}
                  disabled={!selectedServices?.includes(service?.id) && selectedServices?.length >= 3}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 flex items-center gap-2 ${
                    selectedServices?.includes(service?.id)
                      ? 'bg-gradient-to-b from-[#2642B2] to-[#1a365d] text-primary-foreground shadow-md'
                      : 'bg-gradient-to-b from-[#2642B2] to-[#1a365d] text-text-secondary hover:bg-gradient-to-b from-[#2642B2] to-[#1a365d] disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  <Icon name={service?.icon} size={16} />
                  {service?.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Feature</th>
                  {selectedServices?.map((serviceId) => {
                    const service = services?.find(s => s?.id === serviceId);
                    return (
                      <th key={serviceId} className="text-center py-4 px-4">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon name={service?.icon} size={20} color="var(--color-primary)" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">{service?.name}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-4 px-4 text-sm font-medium text-text-secondary">Implementation Time</td>
                  {selectedServices?.map((serviceId) => (
                    <td key={serviceId} className="py-4 px-4 text-center text-sm text-foreground">
                      {comparisonData?.[serviceId]?.implementation}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="py-4 px-4 text-sm font-medium text-text-secondary">Support Level</td>
                  {selectedServices?.map((serviceId) => (
                    <td key={serviceId} className="py-4 px-4 text-center text-sm text-foreground">
                      {comparisonData?.[serviceId]?.support}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="py-4 px-4 text-sm font-medium text-text-secondary">Pricing Model</td>
                  {selectedServices?.map((serviceId) => (
                    <td key={serviceId} className="py-4 px-4 text-center text-sm text-foreground">
                      {comparisonData?.[serviceId]?.pricing}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="py-4 px-4 text-sm font-medium text-text-secondary">Expected ROI</td>
                  {selectedServices?.map((serviceId) => (
                    <td key={serviceId} className="py-4 px-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-success">
                        <Icon name="TrendingUp" size={14} />
                        {comparisonData?.[serviceId]?.roi}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-4 px-4 text-sm font-medium text-text-secondary">Key Features</td>
                  {selectedServices?.map((serviceId) => (
                    <td key={serviceId} className="py-4 px-4">
                      <ul className="space-y-2">
                        {comparisonData?.[serviceId]?.features?.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-xs text-text-secondary">
                            <Icon name="Check" size={12} color="var(--color-success)" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
           <button
  onClick={() => window.open("/pdfs/Orator_Company Profile FY 2026–27.pdf", "_blank")}
  className="bg-gradient-to-b from-[#2642B2] to-[#1a365d] flex-1 text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
>
  <Icon name="FileText" size={20} />
  Download Company Profile PDF
</button>

            <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    const message = "Hello! I need free consultation.";
                    window.open(`https://wa.me/${YOUR_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                  className="flex-1 bg-[#2642B2] text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center  border-2 border-white text-white hover:bg-gradient-to-b from-[#2642B2] to-[#1a365d] w-full sm:w-auto flex items-center gap-2"
                >
                  <img src="/assets/whatsapp.svg" alt="WhatsApp" className="w-5 h-5" />
                  Get Free Consultation on WhatsApp
                </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceComparison;